/**
 * Agent appearance config: the `{ shape, colors, soften, noise, widget_theme }`
 * blob configured in the founding-agent portal and served on the public
 * /config endpoint. Pure and dependency-free; same contract as the portal's
 * `portal/lib/fa-appearance.ts`.
 */
export const APPEARANCE_SHAPES = ["pixel", "flow", "radio", "stripes", "conic"];
export const SOFTEN_MAX = 24;
export const MAX_COLORS = 2;
/** The soften value is authored against a 320px editor stage; smaller
 *  canvases scale it down proportionally so a 48px bubble orb reads the
 *  same as the portal preview instead of blurring to mush. */
export const SOFTEN_REFERENCE_SIZE = 320;
export const DEFAULT_APPEARANCE = {
    shape: "flow",
    colors: ["#ABE6C7", "#BFDBFE"],
    soften: 6,
    noise: 0,
    widget_theme: "light",
};
/** Shape animation-clock multipliers per conversation state. */
export const STATE_SPEEDS = { idle: 0.6, listening: 1.3, talking: 2.4 };
export const CONVERSATION_STATES = ["idle", "listening", "talking"];
export const STATE_STATUS = {
    idle: "Tap to speak",
    listening: "Listening ...",
    talking: "Speaking ...",
};
const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
export function isHexColor(value) {
    return typeof value === "string" && HEX.test(value);
}
/** Uppercase 6-digit form, so `#abc` and `#AABBCC` compare equal. */
export function normalizeHex(hex) {
    let h = hex.replace("#", "");
    if (h.length === 3)
        h = h
            .split("")
            .map((c) => c + c)
            .join("");
    return `#${h.toUpperCase()}`;
}
export function hexToHsl(hex) {
    let h = hex.replace("#", "");
    if (h.length === 3)
        h = h
            .split("")
            .map((c) => c + c)
            .join("");
    const n = parseInt(h, 16);
    const r = ((n >> 16) & 255) / 255;
    const g = ((n >> 8) & 255) / 255;
    const b = (n & 255) / 255;
    const mx = Math.max(r, g, b);
    const mn = Math.min(r, g, b);
    const d = mx - mn;
    let hh = 0;
    let s = 0;
    const l = (mx + mn) / 2;
    if (d) {
        s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
        hh = mx === r ? (g - b) / d + (g < b ? 6 : 0) : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
        hh /= 6;
    }
    return [hh, s, l];
}
export function hslToHex(h, s, l) {
    let r;
    let g;
    let b;
    if (!s) {
        r = g = b = l;
    }
    else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        const f = (t) => {
            if (t < 0)
                t += 1;
            if (t > 1)
                t -= 1;
            if (t < 1 / 6)
                return p + (q - p) * 6 * t;
            if (t < 1 / 2)
                return q;
            if (t < 2 / 3)
                return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };
        r = f(h + 1 / 3);
        g = f(h);
        b = f(h - 1 / 3);
    }
    const to = (x) => Math.round(x * 255)
        .toString(16)
        .padStart(2, "0");
    return `#${to(r)}${to(g)}${to(b)}`;
}
/** Same hue/saturation, lightness nudged by `dL` and clamped. */
export function shade(hex, dL) {
    const [h, s, l] = hexToHsl(hex);
    return hslToHex(h, s, Math.max(0, Math.min(1, l + dL)));
}
/**
 * A single authored colour has nothing to animate between, so it becomes three
 * close shades (+/-16% lightness). Two or more pass through untouched.
 */
export function expandColors(colors) {
    const valid = colors.filter(isHexColor);
    if (valid.length >= 2)
        return valid;
    if (valid.length === 1)
        return [shade(valid[0], 0.16), valid[0], shade(valid[0], -0.16)];
    return [DEFAULT_APPEARANCE.colors[0]];
}
export function rgba(hex, a) {
    let h = hex.replace("#", "");
    if (h.length === 3)
        h = h
            .split("")
            .map((c) => c + c)
            .join("");
    const n = parseInt(h, 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}
function clamp(n, lo, hi) {
    return Math.max(lo, Math.min(hi, n));
}
/**
 * Coerce anything (a fetched config, an init option, `undefined`) into a
 * usable config. Never throws: unknown fields fall back to the defaults so a
 * bad blob degrades to the default orb rather than a broken bubble.
 */
export function normalizeAppearance(value) {
    const raw = (typeof value === "object" && value !== null ? value : {});
    const shape = APPEARANCE_SHAPES.includes(raw.shape)
        ? raw.shape
        : DEFAULT_APPEARANCE.shape;
    const colors = Array.isArray(raw.colors)
        ? raw.colors.filter(isHexColor).slice(0, MAX_COLORS).map(normalizeHex)
        : [];
    const soften = typeof raw.soften === "number" && Number.isFinite(raw.soften)
        ? clamp(Math.round(raw.soften), 0, SOFTEN_MAX)
        : DEFAULT_APPEARANCE.soften;
    const noise = typeof raw.noise === "number" && Number.isFinite(raw.noise)
        ? clamp(raw.noise, 0, 1)
        : DEFAULT_APPEARANCE.noise;
    const widget_theme = raw.widget_theme === "dark" ? "dark" : "light";
    return {
        shape,
        colors: colors.length ? colors : DEFAULT_APPEARANCE.colors,
        soften,
        noise,
        widget_theme,
    };
}
export function appearanceEquals(a, b) {
    return (a.shape === b.shape &&
        a.soften === b.soften &&
        a.noise === b.noise &&
        a.widget_theme === b.widget_theme &&
        a.colors.length === b.colors.length &&
        a.colors.every((c, i) => c === b.colors[i]));
}
/**
 * Whole-circle breathing scale. Driven by wall-clock seconds so the cadence is
 * independent of the shape's animation speed.
 */
export function pulseScale(state, seconds) {
    if (state === "talking")
        return 1 + 0.05 * Math.sin(seconds * 6.5) + 0.022 * Math.sin(seconds * 10.7 + 1);
    if (state === "listening")
        return 1 + 0.012 * Math.sin(seconds * 3.2);
    return 1;
}
/** Advance the shared animation clock by `dt` seconds at the state's pace. */
export function advanceClock(clock, dt, state) {
    return clock + dt * STATE_SPEEDS[state];
}
/**
 * What to draw this tick. `reduced` collapses to the deterministic first frame
 * -- clock zero, no breathing -- which is the `prefers-reduced-motion` render.
 */
export function orbFrame(opts) {
    if (opts.reduced)
        return { t: 0, scale: 1 };
    return { t: opts.clock, scale: pulseScale(opts.state, opts.seconds) };
}
/** Static CSS stand-in for the canvas orb (fallbacks, avatars). */
export function appearanceGradient(colors) {
    const cols = expandColors(colors);
    const stops = cols.map((c, i) => `${c} ${Math.round((i / Math.max(1, cols.length - 1)) * 100)}%`);
    if (cols.length === 1)
        return `radial-gradient(circle at 35% 30%, ${cols[0]}, ${cols[0]})`;
    return `radial-gradient(circle at 35% 30%, ${stops.join(", ")})`;
}
